import { useEffect } from 'react';
import { DEFECT_SEVERITY_ORDER, type DefectSeverity } from '../domain/severity';
import type { ReportOutputLanguage } from '../domain/report';
import { resizeImage } from '../native/imageTools';
import { getMediaAdapter } from '../native/mediaAdapter';
import { scopedStorageKey } from '../persistence/accountScope';
import { V2_REPORT_BRANDING_KEY } from '../persistence/persistenceContract';
import { deleteMediaWithRecords, getMedia, putMediaWithRecords } from '../persistence/v2Database';
import { createStore, useStore } from './createStore';
import { releaseMediaUrls, requestMediaUrl, useMediaUrl } from './mediaUrls';
import { INSPECTOR_AVATAR_MEDIA_KEY } from './inspectorPhoto';
import { currentInspectorProfile } from './preferences';

// The inspector's report branding (Owner decision list 2026-10-19, item 18): company name, telephone,
// e-mail, website and logo on the report cover and in the running header of every PDF page, the
// inspector's own photo beside the inspector details, and the inspector's own names for the three defect
// tiers (item 16: colours fixed, names renamable). PER ACCOUNT, like the inspector profile: a convenience,
// never authority — every read falls back to an empty value and nothing blocks on it.
export const REPORT_LOGO_MEDIA_KEY = 'report-logo';
// A logo prints at most ~1.6 inches wide; 600px keeps it sharp at the PDF's ~192 dpi.
const LOGO_MAX_EDGE = 600;

export type SeverityLabels = Record<ReportOutputLanguage, Partial<Record<DefectSeverity, string>>>;

export interface ReportBranding {
  companyName: string;
  phone: string;
  email: string;
  website: string;
  // When the logo was last set (bytes in the media store under REPORT_LOGO_MEDIA_KEY). null = no logo.
  logoUpdatedAt: number | null;
  // Print the inspector's own photo (Settings › Inspector profile) beside the inspector details.
  showInspectorPhoto: boolean;
  severityLabels: SeverityLabels;
}

export function emptyReportBranding(): ReportBranding {
  return { companyName: '', phone: '', email: '', website: '', logoUpdatedAt: null, showInspectorPhoto: true, severityLabels: { 'en-US': {}, 'zh-CN': {} } };
}

function str(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function labelsOf(value: unknown): Partial<Record<DefectSeverity, string>> {
  const out: Partial<Record<DefectSeverity, string>> = {};
  if (typeof value !== 'object' || value === null) return out;
  for (const key of DEFECT_SEVERITY_ORDER) {
    const label = (value as Record<string, unknown>)[key];
    if (typeof label === 'string' && label.trim().length > 0) out[key] = label.trim().slice(0, 40);
  }
  return out;
}

export function readReportBranding(): ReportBranding {
  let raw: string | null = null;
  try {
    const key = scopedStorageKey(V2_REPORT_BRANDING_KEY);
    raw = key === null ? null : window.localStorage.getItem(key);
  } catch {
    return emptyReportBranding();
  }
  if (raw === null) return emptyReportBranding();
  try {
    const record = JSON.parse(raw) as Record<string, unknown>;
    const labels = (typeof record.severityLabels === 'object' && record.severityLabels !== null ? record.severityLabels : {}) as Record<string, unknown>;
    return {
      companyName: str(record.companyName).trim(),
      phone: str(record.phone).trim(),
      email: str(record.email).trim(),
      website: str(record.website).trim(),
      logoUpdatedAt: typeof record.logoUpdatedAt === 'number' && Number.isFinite(record.logoUpdatedAt) ? record.logoUpdatedAt : null,
      showInspectorPhoto: record.showInspectorPhoto !== false,
      severityLabels: { 'en-US': labelsOf(labels['en-US']), 'zh-CN': labelsOf(labels['zh-CN']) }
    };
  } catch {
    return emptyReportBranding();
  }
}

export const reportBrandingStore = createStore<{ branding: ReportBranding | null }>({ branding: null });

export function writeReportBranding(branding: ReportBranding): ReportBranding {
  const next: ReportBranding = {
    companyName: branding.companyName.trim(),
    phone: branding.phone.trim(),
    email: branding.email.trim(),
    website: branding.website.trim(),
    logoUpdatedAt: branding.logoUpdatedAt,
    showInspectorPhoto: branding.showInspectorPhoto,
    severityLabels: { 'en-US': labelsOf(branding.severityLabels['en-US']), 'zh-CN': labelsOf(branding.severityLabels['zh-CN']) }
  };
  try {
    const key = scopedStorageKey(V2_REPORT_BRANDING_KEY);
    if (key !== null) window.localStorage.setItem(key, JSON.stringify(next));
  } catch {
    // storage refusal must never block field work; the caller still gets the value it asked for
  }
  reportBrandingStore.set(() => ({ branding: next }));
  return next;
}

export function currentReportBranding(): ReportBranding {
  const cached = reportBrandingStore.get().branding;
  if (cached) return cached;
  const fresh = readReportBranding();
  reportBrandingStore.set(() => ({ branding: fresh }));
  return fresh;
}

export function useReportBranding(): ReportBranding {
  const cached = useStore(reportBrandingStore, (s) => s.branding);
  useEffect(() => {
    if (!cached) currentReportBranding();
  }, [cached]);
  return cached ?? readReportBranding();
}

export function resetReportBrandingStoreForTests(): void {
  reportBrandingStore.set(() => ({ branding: null }));
}

export type LogoOutcome = { kind: 'cancelled' } | { kind: 'saved' } | { kind: 'error'; message: string };

// One logo per account, picked from the library (never the camera), stored as PNG so a transparent
// logo stays transparent on the white page.
export async function setReportLogoAction(now: number = Date.now()): Promise<LogoOutcome> {
  try {
    const files = await getMediaAdapter().pickFromLibrary({ single: true });
    const file = files[0];
    if (!file) return { kind: 'cancelled' };
    const resized = await resizeImage(file.blob, { maxEdge: LOGO_MAX_EDGE, type: 'image/png' });
    const blob = resized ? resized.blob : file.blob;
    await putMediaWithRecords(REPORT_LOGO_MEDIA_KEY, { type: blob.type || file.mimeType, bytes: await blob.arrayBuffer() }, null);
  } catch (raw) {
    return { kind: 'error', message: raw instanceof Error ? raw.message : String(raw) };
  }
  writeReportBranding({ ...currentReportBranding(), logoUpdatedAt: now });
  releaseMediaUrls([REPORT_LOGO_MEDIA_KEY]);
  requestMediaUrl(REPORT_LOGO_MEDIA_KEY, 'full');
  return { kind: 'saved' };
}

export async function removeReportLogoAction(): Promise<void> {
  await deleteMediaWithRecords(REPORT_LOGO_MEDIA_KEY);
  writeReportBranding({ ...currentReportBranding(), logoUpdatedAt: null });
  releaseMediaUrls([REPORT_LOGO_MEDIA_KEY]);
}

export function useReportLogoUrl(): string | null {
  const branding = useReportBranding();
  return useMediaUrl(branding.logoUpdatedAt !== null ? REPORT_LOGO_MEDIA_KEY : '', 'full');
}

// What a report prints: the branding as text plus the logo and the inspector photo as data URLs.
export interface ReportBrandingForPrint {
  companyName: string;
  contact: string;
  logo: string | null;
  inspectorPhoto: string | null;
  severityLabels: SeverityLabels;
}

function blobToDataUrl(blob: Blob): Promise<string | null> {
  return new Promise((resolve) => {
    try {
      const reader = new FileReader();
      reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    } catch {
      resolve(null);
    }
  });
}

async function mediaDataUrl(key: string): Promise<string | null> {
  try {
    const blob = (await getMedia(key, 'full')) ?? (await getMedia(key, 'thumbnail'));
    return blob ? blobToDataUrl(blob) : null;
  } catch {
    return null;
  }
}

export async function brandingForPrint(branding: ReportBranding = currentReportBranding()): Promise<ReportBrandingForPrint> {
  const profile = currentInspectorProfile();
  return {
    companyName: branding.companyName,
    contact: [branding.phone, branding.email, branding.website].filter((part) => part.length > 0).join(' · '),
    logo: branding.logoUpdatedAt !== null ? await mediaDataUrl(REPORT_LOGO_MEDIA_KEY) : null,
    inspectorPhoto: branding.showInspectorPhoto && profile.photoUpdatedAt !== null ? await mediaDataUrl(INSPECTOR_AVATAR_MEDIA_KEY) : null,
    severityLabels: branding.severityLabels
  };
}
